import { AppKeys, CoreApp } from '@domain/types';
import DocumentsIcon from '@honestica/ui-kit/assets/icons/DocumentsIcon.svg?react';
import DocumentsIconUnselected from '@honestica/ui-kit/assets/icons/DocumentsIconUnselected.svg?react';
import IntegrationIcon from '@honestica/ui-kit/assets/icons/IntegrationIcon.svg?react';
import IntegrationIconUnselected from '@honestica/ui-kit/assets/icons/IntegrationIconUnselected.svg?react';
import LifenLogo from '@honestica/ui-kit/assets/icons/LifenLogo.svg?react';
import MedicalVisaIcon from '@honestica/ui-kit/assets/icons/MedicalVisaIcon.svg?react';
import MedicalVisaIconUnselected from '@honestica/ui-kit/assets/icons/MedicalVisaIconUnselected.svg?react';
import { colors } from '@honestica/ui-kit/src';
import { APP_HOME_URLS, APP_URLS } from '@launcher/constants';
import { selectActiveApps } from '@store/user/user.selector';
import cx from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './MainSidebar.module.less';
import { SubMenus } from './SubMenus/SubMenus.component';

export const MainSidebar = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const coreApps = useSelector(selectActiveApps);
  const currentApp = useMemo(
    () => coreApps.find((app) => pathname.includes(app.path))?.name,
    [pathname, coreApps],
  );

  const handleSelectApp = (appKey: string) => {
    if (APP_URLS[appKey as AppKeys]) {
      navigate(`${APP_HOME_URLS[appKey as AppKeys]}`);
    }
  };

  const renderAppIcon = (app: CoreApp) => {
    switch (app.name) {
      case AppKeys.Integrations:
        return (
          <span role="img" aria-label={t(app.displayName)} className="anticon">
            <IntegrationIconUnselected className={styles.icon} />
            <IntegrationIcon className={styles.iconSelected} />
          </span>
        );
      case AppKeys.MedicalVisa:
        return (
          <span role="img" aria-label={t(app.displayName)} className="anticon">
            <MedicalVisaIconUnselected color={colors.white[0]} className={styles.icon} />
            <MedicalVisaIcon color={colors.white[0]} className={styles.iconSelected} />
          </span>
        );
      case AppKeys.Documents:
      default:
        return (
          <span role="img" aria-label={t(app.displayName)} className="anticon">
            <DocumentsIconUnselected className={styles.icon} />
            <DocumentsIcon className={styles.iconSelected} />
          </span>
        );
    }
  };

  return (
    <aside data-testid="sidebar-menu" className={styles.sidebarWrapper}>
      <div className={styles.sidebar}>
        <LifenLogo className={styles.logo} />
        <div className={styles.menuWrapper}>
          <div>
            <div className={styles.sidebarApps}>
              {coreApps.map((app, index) => (
                <div
                  aria-current={app.name === currentApp && 'true'}
                  className={styles.app}
                  data-testid={`sidebar-menu-${app.name}`}
                  key={app.name}
                  title={t(app.displayName)}
                  role="button"
                  onClick={() => handleSelectApp(app.name)}
                  tabIndex={index}
                >
                  <div
                    className={cx(
                      {
                        [styles.appButtonSelected]: app.name === currentApp,
                        [styles.appButtonUnselected]: app.name !== currentApp,
                      },
                      styles.appButton,
                    )}
                  >
                    {renderAppIcon(app)}
                  </div>
                  <span className={styles.appLabel}>{t(app.displayName)}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SubMenus />
          </div>
        </div>
      </div>
    </aside>
  );
};
